/**
 * A Public report's access capability, generated by this client - never by the backend
 * (Phase 5 brief §6, mirroring the server's own ADR 0008 design).
 *
 * Exact shape the backend validates: `pr_` followed by 43 URL-safe base64 characters, the
 * unpadded encoding of exactly 32 bytes (256 bits) of randomness. `crypto.getRandomValues`
 * only - never `Math.random()`, which is not a cryptographically secure source. This value
 * is sent as the `X-Orszem-Report-Access` header; it is never logged, put in a URL, or
 * written to `localStorage`/`sessionStorage` (see `storage/db.ts`).
 */
const CREDENTIAL_PATTERN = /^pr_[A-Za-z0-9_-]{43}$/

function toBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  const base64 = btoa(binary)
  return base64.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

/** Generates a fresh, cryptographically random credential. Called once per new report. */
export function generateReportAccessCredential(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  const credential = 'pr_' + toBase64Url(bytes)
  if (!CREDENTIAL_PATTERN.test(credential)) {
    // Would only happen if the encoding above were wrong - fail loudly rather than send
    // something the backend is guaranteed to reject as malformed.
    throw new Error('generated credential does not match the expected shape')
  }
  return credential
}

/** Reconstructs a credential from a value this app already generated and stored, validating its shape. */
export function parseStoredCredential(value: string): string | null {
  return CREDENTIAL_PATTERN.test(value) ? value : null
}

/** Generates a fresh `clientSubmissionId` - a standard random (v4) UUID via the Web Crypto API, never `Math.random()`. */
export function generateClientSubmissionId(): string {
  return crypto.randomUUID()
}
